import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/** ---------- Types ---------- */
// GeoJSON properties we send back from the edge fn (id = voter_id)
type VoterProps = { id: string };
type VoterFC = GeoJSON.FeatureCollection<GeoJSON.Point, VoterProps>;

/** ---------- Config ---------- */
const START = { lat: 26.1, lng: -97.4, zoom: 10 };
const LIMIT = 5000;

// Stadia Maps
const STADIA_KEY =
  (import.meta.env.VITE_STADIA_API_KEY as string | undefined) ||
  (import.meta.env.VITE_STADIA_KEY as string | undefined);

const STADIA_URL = `https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png${
  STADIA_KEY ? `?api_key=${encodeURIComponent(STADIA_KEY)}` : ""
}`;

// Supabase envs
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function edgeUrl(pathWithQuery: string) {
  return `${SUPABASE_URL}/functions/v1${pathWithQuery}`;
}
function debounce<T extends (...args: any[]) => void>(fn: T, ms: number) {
  let t: number | undefined;
  return (...args: Parameters<T>) => {
    if (t) window.clearTimeout(t);
    t = window.setTimeout(() => fn(...args), ms);
  };
}

export default function VoterMap() {
  // 🔵 use global campaign (top bar)
  const campaignId = useCampaignId();

  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.GeoJSON | null>(null);

  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  // Initialize map once
  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map("voter-map", { center: [START.lat, START.lng], zoom: START.zoom });
    mapRef.current = map;

    if (!STADIA_KEY) setErr("Stadia Maps API key is missing. Set VITE_STADIA_API_KEY (or VITE_STADIA_KEY).");

    L.tileLayer(STADIA_URL, {
      attribution:
        '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; ' +
        '<a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; ' +
        '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const layer = L.geoJSON(undefined, {
      onEachFeature: (feature: any, layer: L.Layer) => {
        const id = (feature?.properties?.id ?? "") as string;
        layer.bindPopup(
          `<div style="min-width:160px">
             <div><b>Voter ID</b><br/>${id}</div>
             <div style="margin-top:6px">
               <a href="/voters/${encodeURIComponent(id)}" style="color:#2563eb">Open detail</a>
             </div>
           </div>`
        );
      },
      pointToLayer: (_feature: any, latlng: L.LatLng) => L.circleMarker(latlng, { radius: 4 }),
    });
    layer.addTo(map);
    layerRef.current = layer;

    const debouncedFetch = debounce(() => {
      fetchForBounds().catch((e) => setErr(e.message || String(e)));
    }, 200);

    map.on("moveend", debouncedFetch);
    setTimeout(() => debouncedFetch(), 80);

    return () => {
      map.off("moveend", debouncedFetch);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch GeoJSON for current bounds from Edge Function
  async function fetchForBounds() {
    if (!mapRef.current || !campaignId) return;
    setErr(null);
    setLoading(true);

    const b = mapRef.current.getBounds();
    const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;

    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token ?? ANON_KEY;

    const url = edgeUrl(
      `/voters-geojson?campaign_id=${encodeURIComponent(campaignId)}&bbox=${encodeURIComponent(
        bbox
      )}&limit=${LIMIT}`
    );

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      setLoading(false);
      throw new Error(`Geo fetch failed: ${res.status} ${txt || res.statusText}`);
    }

    const fc = (await res.json()) as VoterFC;

    if (layerRef.current) {
      layerRef.current.clearLayers();
      layerRef.current.addData(fc as unknown as GeoJSON.GeoJsonObject);
    }
    setCount(fc.features.length);
    setLoading(false);
  }

  const totalText = useMemo(
    () => (loading ? "Loading points…" : `${count} point${count === 1 ? "" : "s"} in view`),
    [loading, count]
  );

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Voters Map</h1>

      {err && (
        <div
          style={{
            background: "#ffe8e8",
            border: "1px solid #f2b0b0",
            padding: 8,
            borderRadius: 6,
            marginBottom: 10,
            color: "#a40000",
          }}
        >
          {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        {/* page campaign selector removed — uses top bar */}
        <div style={{ color: "#6b7280", fontSize: 13 }}>{totalText}</div>
      </div>

      <div
        id="voter-map"
        style={{
          height: "72vh",
          width: "100%",
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
        }}
      />
    </div>
  );
}
